import React, { useState, useEffect, useMemo } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, 
  FormControl, InputLabel, Select, MenuItem, Grid, Box, Typography, Tooltip, IconButton, FormLabel
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

const EditCategoryModal = ({ open, onClose, category, onUpdate, locationId, categories = [] }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [color, setColor] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);
  
  // Asegurar que categories sea siempre un array
  const safeCategories = Array.isArray(categories) ? categories : [];

  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setParentId(category.parentId || null);
      setColor(category.color || '');
      setDisplayOrder(category.displayOrder || 0);
    } else {
      // Resetear valores si no hay category
      setName('');
      setParentId(null);
      setColor('');
      setDisplayOrder(0);
    }
  }, [category, open]);

  // Función recursiva para aplanar categorías y construir opciones del select
  const flattenCategories = (cats, level = 0, excludeId = null) => {
    if (!cats || !Array.isArray(cats)) {
      return [];
    }
    let result = [];
    cats.forEach(cat => {
      if (cat && cat.id !== excludeId) {
        const prefix = '  '.repeat(level);
        result.push({ id: cat.id, name: `${prefix}${cat.name}`, level });
        if (cat.children && Array.isArray(cat.children) && cat.children.length > 0) {
          result = result.concat(flattenCategories(cat.children, level + 1, excludeId));
        }
      }
    });
    return result;
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert(t('category_name_required') || 'El nombre de la categoría es requerido');
      return;
    }

    // Validar que no se seleccione a sí misma como padre
    if (parentId && category && parentId === category.id) {
      alert(t('cannot_select_self_as_parent') || 'No se puede seleccionar a sí misma como categoría padre');
      return;
    }
    
    if (!category || !category.id) {
      alert(t('category_not_found') || 'Categoría no encontrada');
      return;
    }

    onUpdate({
      id: category.id,
      name: name.trim(),
      location_id: parseInt(locationId),
      parent_id: parentId || null,
      color: color || null,
      display_order: displayOrder || 0
    });
    onClose();
  };

  const categoryOptions = useMemo(() => {
    try {
      if (!safeCategories || !Array.isArray(safeCategories) || safeCategories.length === 0) {
        return [];
      }
      if (!category || !category.id) {
        return flattenCategories(safeCategories, 0, null);
      }
      const result = flattenCategories(safeCategories, 0, category.id);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error flattening categories:', error);
      return [];
    }
  }, [safeCategories, category?.id]);
  
  // Asegurar que categoryOptions siempre sea un array - con validación más estricta
  const safeCategoryOptions = (() => {
    if (categoryOptions === undefined || categoryOptions === null) {
      return [];
    }
    if (!Array.isArray(categoryOptions)) {
      console.warn('categoryOptions is not an array:', categoryOptions);
      return [];
    }
    return categoryOptions;
  })();

  // Si no hay category, no renderizar el modal
  if (!category) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('edit_category')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label={t('category_name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              margin="dense"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth margin="dense">
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FormLabel sx={{ mr: 1 }}>{t('parent_category') || 'Categoría Padre (Opcional)'}</FormLabel>
                <Tooltip title={t('parent_category_info') || 'La categoría padre es opcional. Permite crear una estructura jerárquica de categorías.'}>
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <InfoIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Select
                value={parentId || ''}
                onChange={(e) => setParentId(e.target.value || null)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t('none') || 'Ninguna (Categoría raíz)'}</em>
                </MenuItem>
                {(() => {
                  const options = Array.isArray(safeCategoryOptions) ? safeCategoryOptions : [];
                  return options.map(opt => {
                    if (!opt || !opt.id) return null;
                    return (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name || ''}
                      </MenuItem>
                    );
                  });
                })()}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {t('parent_category_helper') || 'Seleccione una categoría padre para crear una estructura jerárquica. Opcional.'}
              </Typography>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('color') || 'Color (Opcional)'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <input
                  type="color"
                  value={color || '#000000'}
                  onChange={(e) => setColor(e.target.value)}
                  style={{
                    width: '60px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
                <TextField
                  label={t('color_hex') || 'Color HEX'}
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#FF5733"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 7 }}
                  helperText={t('color_hex_helper') || 'Ingrese el color en formato hexadecimal (ej: #FF5733). Opcional.'}
                />
                {color && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setColor('')}
                  >
                    {t('remove_color') || 'Quitar'}
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('display_order') || 'Orden de Visualización'}</InputLabel>
              <Select
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                label={t('display_order') || 'Orden de Visualización'}
              >
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {t('display_order_helper') || 'Seleccione el orden en que se mostrará esta categoría (0-9). Menor número = aparece primero.'}
              </Typography>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSave} color="primary" variant="contained">{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCategoryModal;
